import { useEffect } from 'react';
import { X, Unlink, Loader2 } from 'lucide-react';
import TransactionRow from './TransactionRow.jsx';
import { useRecurrenceTransactions } from '../hooks/useRecurrences.js';
import { usePatchTransactionRecurrence } from '../hooks/useTransactions.js';
import { formatCurrency } from '../lib/format.js';

const FREQ_LABELS = {
  weekly:    'Hebdomadaire',
  monthly:   'Mensuelle',
  quarterly: 'Trimestrielle',
  yearly:    'Annuelle',
  custom:    'Personnalisée',
};

/**
 * Slide-over qui affiche les paiements historiques d'une récurrence confirmée,
 * avec bouton « Délier » par transaction.
 */
export default function RecurrenceTransactionsPanel({ open, onClose, recurrence }) {
  useEffect(() => {
    if (!open) return;
    function onKey(e) { if (e.key === 'Escape') onClose?.(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const { data, isLoading, error } = useRecurrenceTransactions(open ? recurrence?.id : null);
  const items = data?.items ?? [];
  const total = data?.total ?? items.length;
  const totalAmount = items.reduce((acc, t) => acc + (t.amount_cents ?? 0), 0);

  const displayLabel = recurrence?.label_sample ?? recurrence?.label_pattern ?? '—';
  const freqLabel = FREQ_LABELS[recurrence?.frequency] ?? recurrence?.frequency ?? '';

  return (
    <>
      <div
        className={`
          fixed inset-0 z-40 transition-opacity duration-200
          ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
          bg-black/20 lg:bg-black/0
          lg:right-[560px] lg:left-0 xl:right-[640px]
        `}
        aria-hidden
        onClick={onClose}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Paiements historiques de la récurrence"
        className={`
          fixed z-50 bg-white shadow-2xl
          transition-transform duration-200 ease-out
          inset-x-0 bottom-0 rounded-t-2xl max-h-[92dvh] overflow-y-auto
          ${open ? 'translate-y-0' : 'translate-y-full'}
          lg:inset-x-auto lg:right-0 lg:top-0 lg:bottom-0 lg:h-dvh lg:w-[560px] xl:w-[640px] lg:rounded-none lg:max-h-none lg:overflow-y-auto
          ${open ? 'lg:translate-x-0' : 'lg:translate-x-full'}
        `}
      >
        <div className="flex items-start justify-between border-b border-slate-100 px-4 py-3 sticky top-0 bg-white z-10">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-slate-900 truncate">{displayLabel}</h2>
            <p className="text-xs text-slate-500">
              {freqLabel}
              {recurrence?.amount_cents != null && (
                <> · {formatCurrency(Math.abs(recurrence.amount_cents))}</>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        <div>
          {!open || !recurrence ? null : isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="animate-spin text-slate-400" size={20} />
            </div>
          ) : error ? (
            <p className="text-sm text-red-600 py-4 px-4">Erreur : {error.message}</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-slate-400 py-10 text-center">
              Aucun paiement historique lié à cette récurrence.
            </p>
          ) : (
            <>
              <div className="flex items-center justify-between text-xs text-slate-500 px-4 py-2 border-b border-slate-100">
                <span>{total} transaction{total > 1 ? 's' : ''}</span>
                <span className="font-semibold tabular-nums text-slate-800">
                  Total : {formatCurrency(totalAmount)}
                </span>
              </div>
              <ul className="divide-y divide-slate-100">
                {items.map((tx) => (
                  <TxRow key={tx.id} tx={tx} />
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </>
  );
}

function TxRow({ tx }) {
  const patch = usePatchTransactionRecurrence();
  const onUnlink = () => patch.mutate({ id: tx.id, recurrence_id: null });
  return (
    <li className="relative">
      <TransactionRow transaction={tx} compact onClick={() => {}} />
      <button
        type="button"
        onClick={onUnlink}
        disabled={patch.isPending}
        className="absolute top-2 right-2 text-[11px] text-slate-400 hover:text-red-600 transition-colors disabled:opacity-50 flex items-center gap-1 bg-white/80 backdrop-blur px-1.5 py-0.5 rounded"
        aria-label="Délier cette transaction de la récurrence"
      >
        <Unlink size={11} /> Délier
      </button>
    </li>
  );
}
